import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";
import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import VerifiedAddressSearch from "components/address/VerifiedAddressSearch";
import { post } from "lib/api";
import { useAuth } from "context/AuthContext";
import { getStorefrontConfig } from "lib/storefront";
import { countryForCompany } from "lib/companyProfile";
import { getAppleIdToken, getGoogleAccessToken, getMicrosoftIdToken } from "lib/socialAuth";
import { useMobileLocale } from "mobile/i18n";
import logo from "assets/images/logos/ShortLogo.png";

const socialIconButtonSx = {
  minWidth: 0,
  width: "100%",
  height: 48,
  p: 0,
  borderColor: "rgba(15,23,42,0.12)",
  bgcolor: "#fff",
  borderRadius: 2.2,
  boxShadow: "none",
  "&:hover": { bgcolor: "#fff", borderColor: "rgba(207,36,31,0.35)", boxShadow: "none" },
};

const fiscalRegimeOptions = [
  { value: "601", label: "601 - General de Ley Personas Morales" },
  { value: "606", label: "606 - Arrendamiento" },
  { value: "612", label: "612 - Personas Fisicas con Actividades Empresariales y Profesionales" },
  { value: "621", label: "621 - Incorporacion Fiscal" },
  { value: "622", label: "622 - Actividades Agricolas, Ganaderas, Silvicolas y Pesqueras" },
  { value: "624", label: "624 - Coordinados" },
  { value: "625", label: "625 - Actividades Empresariales con ingresos a traves de Plataformas Tecnologicas" },
  { value: "626", label: "626 - Regimen Simplificado de Confianza" },
];

const cfdiUseOptions = [
  { value: "G01", label: "G01 - Adquisicion de mercancias" },
  { value: "G03", label: "G03 - Gastos en general" },
  { value: "S01", label: "S01 - Sin efectos fiscales" },
];

function MicrosoftMark() {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 7px)", gap: "2px" }}>
      <Box sx={{ width: 7, height: 7, bgcolor: "#F25022" }} />
      <Box sx={{ width: 7, height: 7, bgcolor: "#7FBA00" }} />
      <Box sx={{ width: 7, height: 7, bgcolor: "#00A4EF" }} />
      <Box sx={{ width: 7, height: 7, bgcolor: "#FFB900" }} />
    </Box>
  );
}

const resolveRedirectUriForCurrentHost = (configured, fallbackPath) => {
  const fallback = `${window.location.origin}${fallbackPath}`;
  const value = String(configured || "").trim();
  if (!value) return fallback;
  try {
    const parsed = new URL(value);
    if (parsed.host !== window.location.host) {
      const nextPath = parsed.pathname || fallbackPath;
      return `${window.location.origin}${nextPath}`;
    }
    return parsed.toString();
  } catch {
    return fallback;
  }
};

export default function RegisterCustomer() {
  const storefront = getStorefrontConfig();
  const mobileLocale = useMobileLocale();
  const isMx = String(storefront?.locale || "").toLowerCase().startsWith("es") || mobileLocale?.isSpanish;
  const requiresMxFiscal = countryForCompany(storefront?.companyKey) === "MX";
  const [searchParams] = useSearchParams();
  const text = isMx
    ? {
        title: "Crear cuenta",
        distributorTitle: "Solicitud de distribuidor",
        subtitle: "Crea tu cuenta.",
        name: "Nombre del cliente",
        accountType: "Tipo de cuenta",
        customerAccount: "Cliente final",
        distributorAccount: "Distribuidor",
        businessName: "Razón social / negocio",
        contactName: "Nombre del contacto",
        email: "Correo",
        password: "Contraseña",
        confirm: "Confirmar contraseña",
        phone: "Teléfono",
        address: "Dirección completa",
        addressHelp: "Usa calle, ciudad, estado, código postal y país en un solo campo.",
        addressTypeHint: "Escribe para ver sugerencias verificadas.",
        addressLoading: "Buscando direcciones...",
        addressNoOptions: "Sin sugerencias para esta dirección.",
        addressMinChars: "Escribe más para buscar direcciones.",
        addressVerified: "Dirección verificada",
        addressNeedsVerification: "Selecciona una dirección sugerida para continuar.",
        fiscalInfo: "Información de facturación SAT",
        rfc: "RFC",
        legalName: "Razón social SAT",
        taxSystem: "Régimen fiscal para facturación",
        cfdiUse: "Uso CFDI para compras",
        fiscalZip: "Código postal fiscal",
        billingEmail: "Correo de facturación",
        fiscalAddress: "Domicilio fiscal",
        emailCode: "Código de correo",
        sendCode: "Enviar código",
        sendingCode: "Enviando...",
	        codeSent: "Código enviado. Revisa tu correo.",
	        stepAccount: "Cuenta",
	        stepBusiness: "Negocio",
	        stepFiscal: "SAT",
	        stepVerify: "Verificar",
	        next: "Continuar",
	        previous: "Atrás",
	        submitApplication: "Enviar solicitud",
	        verificationTitle: "Verifica tu correo",
	        verificationHelp: "Enviaremos un código temporal a este correo y lo usaremos para crear tu solicitud.",
	        approvalSubmitted: "Gracias. Tu solicitud de distribuidor está en espera de aprobación. Normalmente toma hasta 24 horas configurarla.",
        distributorLink: "Distribuidores",
        customerLinkPrefix: "¿Eres distribuidor?",
        create: "Crear cuenta",
        creating: "Creando…",
        back: "Volver a iniciar sesión",
      }
    : {
        title: "Create account",
        distributorTitle: "Distributor application",
        subtitle: "Create your account.",
        name: "Customer name",
        accountType: "Account type",
        customerAccount: "End customer",
        distributorAccount: "Distributor business",
        businessName: "Business name",
        contactName: "Primary contact name",
        email: "Email",
        password: "Password",
        confirm: "Confirm password",
        phone: "Phone",
        address: "Full address",
        addressHelp: "Use street, city, state/province, postal code, and country in one field.",
        addressTypeHint: "Type to get verified suggestions.",
        addressLoading: "Looking up addresses...",
        addressNoOptions: "No suggestions for this address.",
        addressMinChars: "Type more to search addresses.",
        addressVerified: "Address verified",
        addressNeedsVerification: "Select a suggested address to continue.",
        fiscalInfo: "SAT billing information",
        rfc: "RFC / Tax ID",
        legalName: "SAT legal name",
        taxSystem: "Fiscal regime for billing",
        cfdiUse: "CFDI use for purchases",
        fiscalZip: "Fiscal ZIP",
        billingEmail: "Billing email",
        fiscalAddress: "Fiscal address",
        emailCode: "Email code",
        sendCode: "Send code",
        sendingCode: "Sending...",
	        codeSent: "Code sent. Check your email.",
	        stepAccount: "Account",
	        stepBusiness: "Business",
	        stepFiscal: "SAT",
	        stepVerify: "Verify",
	        next: "Continue",
	        previous: "Back",
	        submitApplication: "Submit application",
	        verificationTitle: "Verify your email",
	        verificationHelp: "We will send a temporary code to this email and use it to create your application.",
	        approvalSubmitted: "Thank you. Your distributor application is awaiting approval. Setup usually takes up to 24 hours.",
        distributorLink: "Distributors",
        customerLinkPrefix: "Are you a distributor?",
        create: "Create account",
        creating: "Creating...",
        back: "Back to sign in",
      };

  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithSocial } = useAuth();
  const googleClientId = String(
    process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || ""
  ).trim();
  const appleClientId = String(process.env.REACT_APP_APPLE_CLIENT_ID || "").trim();
  const microsoftClientId = String(process.env.REACT_APP_MICROSOFT_CLIENT_ID || "").trim();
  const microsoftTenantId = String(process.env.REACT_APP_MICROSOFT_TENANT_ID || "common").trim() || "common";
  const appleRedirectUri = resolveRedirectUriForCurrentHost(
    process.env.REACT_APP_APPLE_REDIRECT_URI,
    "/authentication/sign-in"
  );
  const microsoftRedirectUri = resolveRedirectUriForCurrentHost(
    process.env.REACT_APP_MICROSOFT_REDIRECT_URI,
    "/msal-popup.html"
  );
  const pathname = String(location?.pathname || "").trim().toLowerCase();
  const isMobileRoute = pathname === "/m/register" || pathname.startsWith("/m/");
  const toMobileAwarePath = (path) => (isMobileRoute ? `/m${path === "/" ? "" : path}` : path);
  const isDistributorApplyRoute =
    pathname === "/distributor/apply" ||
    pathname === "/partners/apply" ||
    pathname === "/m/distributor/apply" ||
    pathname === "/m/partners/apply";
  const queryAccountType = String(searchParams.get("accountType") || "").trim().toLowerCase();
  const queryNext = String(searchParams.get("next") || "").trim();
  const inferredAccountTypeFromNext = queryNext.includes("mode=distributor-sold") ? "distributor" : "customer";
  const initialAccountType = isDistributorApplyRoute
    ? "distributor"
    : queryAccountType === "distributor" || queryAccountType === "customer"
    ? queryAccountType
    : inferredAccountTypeFromNext;
  const accountTypeLocked = isDistributorApplyRoute || queryAccountType === "distributor" || queryAccountType === "customer";
  const showAccountTypeControl = accountTypeLocked || queryNext.includes("mode=distributor-sold");
  const redirectAfterRegister = queryNext.startsWith("/m/") ? queryNext : queryNext.startsWith("/") ? queryNext : "/post-login";

  const [accountType, setAccountType] = useState(initialAccountType);
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [rfc, setRfc] = useState("");
  const [taxLegalName, setTaxLegalName] = useState("");
  const [regimenFiscal, setRegimenFiscal] = useState("");
  const [usoCfdi, setUsoCfdi] = useState("");
  const [fiscalZip, setFiscalZip] = useState("");
  const [facturacionEmail, setFacturacionEmail] = useState("");
  const [fiscalAddressLine, setFiscalAddressLine] = useState("");
  const [emailVerificationCode, setEmailVerificationCode] = useState("");
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [phone, setPhone] = useState("");
  const [addressLine, setAddressLine] = useState("");
	  const [addressLookupLoading, setAddressLookupLoading] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState("");
  const [addressCandidates, setAddressCandidates] = useState([]);
  const [selectedAddressPlaceId, setSelectedAddressPlaceId] = useState("");
  const [selectedAddress, setSelectedAddress] = useState(null);
  const addressLookupSeqRef = useRef(0);
  const [fiscalAddressLookupLoading, setFiscalAddressLookupLoading] = useState(false);
  const [fiscalAddressLookupError, setFiscalAddressLookupError] = useState("");
  const [fiscalAddressCandidates, setFiscalAddressCandidates] = useState([]);
  const [selectedFiscalAddressPlaceId, setSelectedFiscalAddressPlaceId] = useState("");
  const [selectedFiscalAddress, setSelectedFiscalAddress] = useState(null);
	  const fiscalAddressLookupSeqRef = useRef(0);
	  const [distributorStep, setDistributorStep] = useState(0);
	  const [loading, setLoading] = useState(false);
	  const [message, setMessage] = useState(null); // { type, text }
	  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
	  const isDistributorAccount = accountType === "distributor";
	  const distributorSteps = requiresMxFiscal
    ? [text.stepAccount, text.stepBusiness, text.stepFiscal, text.stepVerify]
    : [text.stepAccount, text.stepBusiness, text.stepVerify];
  const fiscalStepIndex = requiresMxFiscal ? 2 : -1;
  const verifyStepIndex = requiresMxFiscal ? 3 : 2;

	  useEffect(() => {
	    setAccountType(initialAccountType);
	  }, [initialAccountType]);

	  useEffect(() => {
	    if (!isDistributorAccount) setDistributorStep(0);
	  }, [isDistributorAccount]);

  const getErrorMessage = (err) => {
    const data = err?.response?.data;
    // Strapi v4/v5 common shapes:
    // - { error: { message } }
    // - { data: null, error: { message } }
    // - { message: "..." }
    if (typeof data?.error === "string") return data.error;
    if (typeof data?.error?.message === "string") return data.error.message;
    if (typeof data?.message === "string") return data.message;
    if (typeof err?.message === "string") return err.message;
    return "Registration failed";
  };

	  const canSubmit = useMemo(() => {
    if (!name.trim()) return false;
    if (isDistributorAccount && !businessName.trim()) return false;
    if (isDistributorAccount && requiresMxFiscal && !rfc.trim()) return false;
    if (isDistributorAccount && requiresMxFiscal && !taxLegalName.trim()) return false;
    if (isDistributorAccount && requiresMxFiscal && !regimenFiscal.trim()) return false;
    if (isDistributorAccount && requiresMxFiscal && !usoCfdi.trim()) return false;
    if (isDistributorAccount && requiresMxFiscal && !fiscalZip.trim()) return false;
	    if (isDistributorAccount && requiresMxFiscal && !facturacionEmail.trim()) return false;
	    if (isDistributorAccount && requiresMxFiscal && !fiscalAddressLine.trim()) return false;
	    if (isDistributorAccount && requiresMxFiscal && !selectedFiscalAddressPlaceId.trim()) return false;
	    if (isDistributorAccount && !emailVerificationCode.trim()) return false;
    if (!email.trim()) return false;
    if (!password) return false;
    if (password !== confirm) return false;
    if (!addressLine.trim()) return false;
    if (!selectedAddressPlaceId.trim()) return false;
    return true;
  }, [
    name,
    businessName,
    rfc,
    taxLegalName,
    regimenFiscal,
    usoCfdi,
    fiscalZip,
    facturacionEmail,
	    fiscalAddressLine,
	    selectedFiscalAddressPlaceId,
	    emailVerificationCode,
    email,
    password,
    confirm,
    addressLine,
    selectedAddressPlaceId,
    isDistributorAccount,
    requiresMxFiscal,
  ]);

  const selectedAddressCandidate = useMemo(() => {
    if (selectedAddress && String(selectedAddress?.placeId || "") === String(selectedAddressPlaceId || "")) return selectedAddress;
    return addressCandidates.find((row) => String(row?.placeId || "") === String(selectedAddressPlaceId || "")) || null;
  }, [addressCandidates, selectedAddress, selectedAddressPlaceId]);

  const selectedFiscalAddressCandidate = useMemo(
    () =>
      (selectedFiscalAddress && String(selectedFiscalAddress?.placeId || "") === String(selectedFiscalAddressPlaceId || "") ? selectedFiscalAddress : null) ||
      fiscalAddressCandidates.find((row) => String(row?.placeId || "") === String(selectedFiscalAddressPlaceId || "")) ||
      null,
    [fiscalAddressCandidates, selectedFiscalAddress, selectedFiscalAddressPlaceId]
  );

  const registrationAddressLookup = useMemo(
    () => ({
      candidatesByKey: { registrationAddress: addressCandidates },
      draftsByKey: { registrationAddress: addressLine },
      loadingByKey: { registrationAddress: addressLookupLoading },
      errorsByKey: { registrationAddress: addressLookupError },
      setDraftForRow: (_rowKey, value) => setAddressLine(String(value || "")),
      applyCandidateForRow: () => {},
    }),
    [addressCandidates, addressLine, addressLookupLoading, addressLookupError]
  );

  const registrationFiscalAddressLookup = useMemo(
    () => ({
      candidatesByKey: { registrationFiscalAddress: fiscalAddressCandidates },
      draftsByKey: { registrationFiscalAddress: fiscalAddressLine },
      loadingByKey: { registrationFiscalAddress: fiscalAddressLookupLoading },
      errorsByKey: { registrationFiscalAddress: fiscalAddressLookupError },
      setDraftForRow: (_rowKey, value) => setFiscalAddressLine(String(value || "")),
      applyCandidateForRow: () => {},
    }),
    [fiscalAddressCandidates, fiscalAddressLine, fiscalAddressLookupLoading, fiscalAddressLookupError]
  );

	  const distributorStepValid = useMemo(() => {
	    if (!isDistributorAccount) return true;
	    if (distributorStep === 0) {
	      return !!name.trim() && !!email.trim() && !!password && password === confirm;
	    }
	    if (distributorStep === 1) {
	      return !!businessName.trim() && !!phone.trim() && !!addressLine.trim() && !!selectedAddressPlaceId.trim();
	    }
	    if (distributorStep === fiscalStepIndex && requiresMxFiscal) {
	      return (
	        !!rfc.trim() &&
	        !!taxLegalName.trim() &&
	        !!regimenFiscal.trim() &&
	        !!usoCfdi.trim() &&
	        !!fiscalZip.trim() &&
	        !!facturacionEmail.trim() &&
	        !!fiscalAddressLine.trim() &&
	        !!selectedFiscalAddressPlaceId.trim()
	      );
	    }
	    return !!emailVerificationCode.trim();
	  }, [
	    isDistributorAccount,
	    distributorStep,
	    name,
	    email,
	    password,
	    confirm,
	    businessName,
	    phone,
	    addressLine,
	    selectedAddressPlaceId,
	    rfc,
	    taxLegalName,
	    regimenFiscal,
	    usoCfdi,
	    fiscalZip,
	    facturacionEmail,
	    fiscalAddressLine,
	    selectedFiscalAddressPlaceId,
	    emailVerificationCode,
      fiscalStepIndex,
      requiresMxFiscal,
	  ]);

  const selectAddressCandidate = (candidate) => {
    const formatted = String(candidate?.formattedAddress || candidate?.addressLine || "").trim();
    const placeId = String(candidate?.placeId || "").trim();
    if (!formatted || !placeId) return;
    addressLookupSeqRef.current += 1;
    setSelectedAddress(candidate);
    setAddressCandidates((prev) => [candidate, ...prev.filter((row) => String(row?.placeId || "") !== placeId)]);
    setAddressLine(formatted);
    setSelectedAddressPlaceId(placeId);
    setAddressLookupError("");
  };

	  const selectFiscalAddressCandidate = (candidate) => {
    const formatted = String(candidate?.formattedAddress || candidate?.addressLine || "").trim();
    const placeId = String(candidate?.placeId || "").trim();
    if (!formatted || !placeId) return;
    fiscalAddressLookupSeqRef.current += 1;
    setSelectedFiscalAddress(candidate);
    setFiscalAddressCandidates((prev) => [candidate, ...prev.filter((row) => String(row?.placeId || "") !== placeId)]);
    setFiscalAddressLine(formatted);
    setSelectedFiscalAddressPlaceId(placeId);
	    setFiscalAddressLookupError("");
	    const postalCode = String(candidate?.address?.postalCode || candidate?.postalCode || "").trim();
	    if (postalCode) setFiscalZip(postalCode);
	  };

  useEffect(() => {
    const value = String(addressLine || "").trim();
    setAddressLookupError("");
    const selectedFormatted = String(selectedAddress?.formattedAddress || selectedAddress?.addressLine || "").trim();
    if (selectedAddressPlaceId && selectedFormatted && selectedFormatted === value) {
      setAddressLookupLoading(false);
      return undefined;
    }
    if (!value || value.length < 6) {
      setAddressCandidates([]);
      setAddressLookupLoading(false);
      return undefined;
    }

    const requestSeq = addressLookupSeqRef.current + 1;
    addressLookupSeqRef.current = requestSeq;
    const timer = setTimeout(async () => {
      try {
        setAddressLookupLoading(true);
        const response = await post("/api/storefront/address/lookup", { addressLine: value });
        if (addressLookupSeqRef.current !== requestSeq) return;
        const candidates = Array.isArray(response?.candidates) ? response.candidates : [];
        setAddressCandidates(candidates);
        const stillSelected = selectedAddressPlaceId && selectedFormatted && selectedFormatted === value;
        if (!stillSelected && !candidates.some((row) => String(row?.placeId || "") === String(selectedAddressPlaceId || ""))) {
          setSelectedAddressPlaceId("");
          setSelectedAddress(null);
        }
      } catch (err) {
        if (addressLookupSeqRef.current !== requestSeq) return;
        setAddressCandidates([]);
        setSelectedAddressPlaceId("");
        setSelectedAddress(null);
        setAddressLookupError(getErrorMessage(err));
      } finally {
        if (addressLookupSeqRef.current === requestSeq) {
          setAddressLookupLoading(false);
        }
      }
    }, 350);
	    return () => clearTimeout(timer);
  }, [addressLine, selectedAddress, selectedAddressPlaceId]);

  useEffect(() => {
    const value = String(fiscalAddressLine || "").trim();
    setFiscalAddressLookupError("");
    const selectedFormatted = String(selectedFiscalAddress?.formattedAddress || selectedFiscalAddress?.addressLine || "").trim();
    if (selectedFiscalAddressPlaceId && selectedFormatted && selectedFormatted === value) {
      setFiscalAddressLookupLoading(false);
      return undefined;
    }
    if (!value || value.length < 6) {
      setFiscalAddressCandidates([]);
	      setFiscalAddressLookupLoading(false);
	      return undefined;
	    }

	    const requestSeq = fiscalAddressLookupSeqRef.current + 1;
	    fiscalAddressLookupSeqRef.current = requestSeq;
	    const timer = setTimeout(async () => {
	      try {
	        setFiscalAddressLookupLoading(true);
        const response = await post("/api/storefront/address/lookup", { addressLine: value });
        if (fiscalAddressLookupSeqRef.current !== requestSeq) return;
        const candidates = Array.isArray(response?.candidates) ? response.candidates : [];
        setFiscalAddressCandidates(candidates);
        const stillSelected = selectedFiscalAddressPlaceId && selectedFormatted && selectedFormatted === value;
        if (!stillSelected && !candidates.some((row) => String(row?.placeId || "") === String(selectedFiscalAddressPlaceId || ""))) {
          setSelectedFiscalAddressPlaceId("");
          setSelectedFiscalAddress(null);
        }
      } catch (err) {
        if (fiscalAddressLookupSeqRef.current !== requestSeq) return;
        setFiscalAddressCandidates([]);
        setSelectedFiscalAddressPlaceId("");
        setSelectedFiscalAddress(null);
        setFiscalAddressLookupError(getErrorMessage(err));
	      } finally {
	        if (fiscalAddressLookupSeqRef.current === requestSeq) {
	          setFiscalAddressLookupLoading(false);
	        }
	      }
	    }, 350);
	    return () => clearTimeout(timer);
  }, [fiscalAddressLine, selectedFiscalAddress, selectedFiscalAddressPlaceId]);

	  const onSubmit = async (e) => {
	    e.preventDefault();
	    setMessage(null);
	    if (isDistributorAccount && distributorStep < distributorSteps.length - 1) {
	      if (distributorStepValid) setDistributorStep((step) => Math.min(step + 1, distributorSteps.length - 1));
	      return;
	    }
	    if (!canSubmit) return;
    try {
      setLoading(true);
      const result = await post("/api/storefront/auth/register", {
        email: String(email || "").trim().toLowerCase(),
        password,
        companyKey: storefront?.companyKey || undefined,
        name: String(name || "").trim(),
        customerKind: isDistributorAccount ? "distributor" : "end_user",
        businessName: isDistributorAccount ? String(businessName || "").trim() : undefined,
        rfc: isDistributorAccount && requiresMxFiscal ? String(rfc || "").trim().toUpperCase() : undefined,
        taxId: isDistributorAccount && requiresMxFiscal ? String(rfc || "").trim().toUpperCase() : undefined,
        taxLegalName: isDistributorAccount && requiresMxFiscal ? String(taxLegalName || "").trim() : undefined,
        regimenFiscal: isDistributorAccount && requiresMxFiscal ? String(regimenFiscal || "").trim() : undefined,
        usoCfdi: isDistributorAccount && requiresMxFiscal ? String(usoCfdi || "").trim() : undefined,
        fiscalZip: isDistributorAccount && requiresMxFiscal ? String(fiscalZip || "").trim() : undefined,
        facturacionEmail: isDistributorAccount && requiresMxFiscal ? String(facturacionEmail || "").trim().toLowerCase() : undefined,
	        fiscalAddress: isDistributorAccount && requiresMxFiscal
	          ? {
	              formattedAddress: String(fiscalAddressLine || "").trim(),
	              street:
	                selectedFiscalAddressCandidate?.address?.street ||
	                selectedFiscalAddressCandidate?.street ||
	                String(fiscalAddressLine || "").trim(),
	              city: selectedFiscalAddressCandidate?.address?.city || selectedFiscalAddressCandidate?.city || undefined,
	              state: selectedFiscalAddressCandidate?.address?.state || selectedFiscalAddressCandidate?.state || undefined,
	              postalCode: String(fiscalZip || "").trim(),
	              country:
	                selectedFiscalAddressCandidate?.address?.country ||
	                selectedFiscalAddressCandidate?.country ||
	                selectedAddressCandidate?.address?.country ||
	                selectedAddressCandidate?.country ||
	                undefined,
	              placeId: String(selectedFiscalAddressPlaceId || "").trim(),
	            }
	          : undefined,
        emailVerificationCode: isDistributorAccount ? String(emailVerificationCode || "").trim() : undefined,
        phone: String(phone || "").trim(),
        addressLine: String(addressLine || "").trim(),
        addressPlaceId: String(selectedAddressPlaceId || "").trim(),
      });

      if (result?.approval?.required) {
        setApplicationSubmitted(true);
        setMessage(null);
        setPassword("");
        setConfirm("");
        return;
      }

      try {
        const ok = await login(String(email || "").trim().toLowerCase(), password);
        if (ok) {
          navigate(redirectAfterRegister, { replace: true });
        } else {
          setMessage({ type: "success", text: "Account created. Please sign in." });
          navigate(toMobileAwarePath("/authentication/sign-in"), { replace: true });
        }
      } catch {
        setMessage({ type: "success", text: "Account created. Please sign in." });
        navigate(toMobileAwarePath("/authentication/sign-in"), { replace: true });
      }
    } catch (err) {
      setMessage({ type: "error", text: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  const onSendCode = async () => {
    const target = String(email || "").trim().toLowerCase();
    if (!target) {
      setMessage({ type: "error", text: isMx ? "Correo requerido." : "Email is required." });
      return;
    }
    try {
      setCodeLoading(true);
      setMessage(null);
      await post("/api/storefront/auth/email-code", {
        email: target,
        purpose: "distributor_registration",
      });
      setCodeSent(true);
      setMessage({ type: "success", text: text.codeSent });
    } catch (err) {
      setMessage({ type: "error", text: getErrorMessage(err) });
    } finally {
      setCodeLoading(false);
    }
  };

  const onSocialRegister = async (provider) => {
    const normalized = String(provider || "").trim().toLowerCase();
    if (!normalized) return;
    const providerEnabled =
      (normalized === "google" && !!googleClientId) ||
      (normalized === "apple" && !!appleClientId) ||
      (normalized === "microsoft" && !!microsoftClientId);
    if (!providerEnabled) {
      setMessage({ type: "error", text: `${normalized[0].toUpperCase()}${normalized.slice(1)} auth is not configured yet.` });
      return;
    }
    if (isDistributorAccount && !businessName.trim()) {
      setMessage({ type: "error", text: isMx ? "La razón social es obligatoria." : "Business name is required." });
      return;
    }
    if (!selectedAddressPlaceId.trim()) {
      setMessage({ type: "error", text: text.addressNeedsVerification });
      return;
    }
    try {
      setLoading(true);
      setMessage(null);

      let payload = {};
      if (normalized === "google") {
        payload = await getGoogleAccessToken({ clientId: googleClientId });
      } else if (normalized === "apple") {
        payload = await getAppleIdToken({ clientId: appleClientId, redirectURI: appleRedirectUri });
      } else if (normalized === "microsoft") {
        payload = await getMicrosoftIdToken({
          clientId: microsoftClientId,
          tenantId: microsoftTenantId,
          redirectUri: microsoftRedirectUri,
        });
      } else {
        throw new Error("Unsupported provider");
      }

      const socialRequest = {
        provider: normalized,
        ...payload,
        accountType,
        customerKind: isDistributorAccount ? "distributor" : "end_user",
        name: String(name || "").trim() || undefined,
        businessName: isDistributorAccount ? String(businessName || "").trim() : undefined,
        phone: String(phone || "").trim() || undefined,
        addressLine: String(addressLine || "").trim() || undefined,
        addressPlaceId: String(selectedAddressPlaceId || "").trim() || undefined,
        country:
          String(selectedAddressCandidate?.address?.country || selectedAddressCandidate?.country || "").trim() ||
          undefined,
        companyKey: storefront?.companyKey || undefined,
      };

      if (isDistributorAccount) {
        const result = await post("/api/storefront/auth/social", socialRequest);
        if (result?.approval?.required) {
          setMessage({
            type: "success",
            text: isMx
              ? "Solicitud de distribuidor enviada. Un administrador debe aprobar la cuenta antes de habilitar el acceso de distribuidor."
              : "Distributor application submitted. An administrator must approve the account before distributor access is enabled.",
          });
          setApplicationSubmitted(true);
          return;
        }
      }

      const ok = await loginWithSocial(socialRequest);

      if (ok) {
        navigate(redirectAfterRegister, { replace: true });
      } else {
        setMessage({ type: "error", text: isMx ? "No se pudo autenticar con social." : "Social auth failed." });
      }
    } catch (err) {
      setMessage({ type: "error", text: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  if (applicationSubmitted) {
    return (
      <Box
        sx={{
          minHeight: "100dvh",
          bgcolor: "#fff",
          color: "#0b1118",
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            minHeight: "100dvh",
            px: 2,
            py: 2.5,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 82,
              height: 82,
              mx: "auto",
              mb: 2,
              borderRadius: "50%",
              bgcolor: "rgba(22,163,74,0.12)",
              color: "#15803d",
              display: "grid",
              placeItems: "center",
            }}
          >
            <Icon sx={{ fontSize: 42 }}>check</Icon>
          </Box>
          <MDTypography variant="h2" fontWeight={900} sx={{ color: "#0b1118", lineHeight: 0.98, letterSpacing: 0 }} gutterBottom>
            {isMx ? "Solicitud enviada" : "Application submitted"}
          </MDTypography>
          <MDTypography variant="body1" sx={{ color: "#667085", lineHeight: 1.7, mb: 2 }}>
            {isMx
              ? "Tu solicitud de distribuidor está en revisión. Un administrador debe aprobar tu cuenta antes de habilitar el acceso de distribuidor."
              : "Your distributor account request is in review. An administrator must approve your account before distributor access is enabled."}
          </MDTypography>
          <Box
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: 2,
              textAlign: "left",
              bgcolor: "#f6f7f9",
              border: "1px solid rgba(11,17,24,0.1)",
            }}
          >
            <MDTypography variant="button" fontWeight={900} sx={{ color: "#0b1118", display: "block", mb: 0.5 }}>
              {isMx ? "Siguiente paso" : "What happens next"}
            </MDTypography>
            <MDTypography variant="body2" sx={{ color: "#667085", lineHeight: 1.6 }}>
              {isMx
                ? "Te enviaremos un correo cuando tu cuenta haya sido aprobada."
                : "You will receive an email when your account has been approved."}
            </MDTypography>
            <MDTypography variant="caption" fontWeight={800} sx={{ color: "#b91c1c", display: "block", mt: 1 }}>
              {String(email || "").trim().toLowerCase()}
            </MDTypography>
          </Box>
          <MDButton
            variant="contained"
            fullWidth
            sx={{
              bgcolor: "#cf241f",
              color: "#fff",
              borderRadius: 2,
              py: 1.35,
              fontWeight: 900,
              boxShadow: "none",
              "&:hover": { bgcolor: "#af1f1a", boxShadow: "none" },
            }}
            onClick={() => navigate(toMobileAwarePath("/authentication/sign-in"), { replace: true })}
          >
            {isMx ? "Volver a iniciar sesión" : "Back to sign in"}
          </MDButton>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: "#fbfbfa",
        color: "#0b1118",
      }}
    >
      <Container
        maxWidth="sm"
        sx={{
          px: 2,
          pt: isMobileRoute ? "calc(env(safe-area-inset-top) + 16px)" : 2,
          pb: 5,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            bgcolor: "#fff",
            minHeight: 52,
            "& fieldset": { borderColor: "rgba(11,17,24,0.12)" },
            "&:hover fieldset": { borderColor: "rgba(207,36,31,0.35)" },
            "&.Mui-focused fieldset": { borderColor: "#cf241f" },
          },
          "& .MuiInputLabel-root.Mui-focused": { color: "#cf241f" },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ mb: 3.5 }}>
          <Box
            component="img"
            src={logo}
            alt="vbspecialties.com"
            sx={{ width: 82, height: "auto", objectFit: "contain", display: "block" }}
          />
          <MDButton variant="text" sx={{ color: "#0b1118", fontWeight: 900, minWidth: "auto", px: 0.5 }} onClick={() => navigate("/m")}>
            Home
          </MDButton>
        </Stack>

        <MDTypography
          variant="h1"
          fontWeight={900}
          sx={{
            color: "#0b1118",
            fontSize: "clamp(2rem, 8vw, 2.7rem)",
            lineHeight: 1.02,
            letterSpacing: 0,
            mb: 2.25,
          }}
        >
          {isDistributorAccount ? (isMx ? "Distribuidor." : "Distributor.") : (isMx ? "Crear cuenta." : "Create account.")}
        </MDTypography>

        {message ? (
          <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2, borderRadius: 2 }}>
            {message.text}
          </Alert>
        ) : null}

        <MDBox
          component="form"
          onSubmit={onSubmit}
          sx={{
            "& .MuiInputBase-input, & input, & textarea, & select": {
              fontSize: "16px !important",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.95rem",
            },
          }}
        >
	          {isDistributorAccount ? (
		            <Stack direction="row" spacing={0.8} sx={{ mb: 2.25, overflowX: "auto", pb: 0.5 }}>
		              {distributorSteps.map((step, index) => (
		                <Chip
	                  key={step}
	                  size="small"
	                  label={`${index + 1}. ${step}`}
	                  color={index === distributorStep ? "info" : "default"}
	                  variant={index === distributorStep ? "filled" : "outlined"}
	                  sx={{
		                    borderRadius: 1.6,
		                    fontWeight: 900,
		                    bgcolor: index === distributorStep ? "#0b1118" : index < distributorStep ? "rgba(15,118,110,0.12)" : "#f6f7f9",
		                    color: index === distributorStep ? "#fff" : index < distributorStep ? "#0f766e" : "#667085",
		                    borderColor: "transparent",
		                    flexShrink: 0,
		                  }}
		                />
	              ))}
	            </Stack>
	          ) : null}
	          <Grid container spacing={2}>
	            {showAccountTypeControl ? (
              <Grid item xs={12}>
                {accountTypeLocked ? (
                  <TextField
                    fullWidth
                    label={text.accountType}
                    value={isDistributorAccount ? text.distributorAccount : text.customerAccount}
                    disabled
                  />
                ) : (
                  <FormControl fullWidth>
                    <InputLabel id="register-account-type-label">{text.accountType}</InputLabel>
                    <Select
                      labelId="register-account-type-label"
                      label={text.accountType}
                      value={accountType}
                      onChange={(e) => setAccountType(String(e.target.value || "customer"))}
                      disabled={loading}
                    >
                      <MenuItem value="customer">{text.customerAccount}</MenuItem>
                      <MenuItem value="distributor">{text.distributorAccount}</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Grid>
            ) : null}
	            {isDistributorAccount && distributorStep === 0 ? (
	              <Grid item xs={12}>
	                <MDTypography variant="button" fontWeight={800} sx={{ color: "#0f172a" }}>
	                  {text.stepAccount}
	                </MDTypography>
	              </Grid>
	            ) : null}
	            {(!isDistributorAccount || distributorStep === 0) ? (
	            <>
	            <Grid item xs={12}>
	              <MDInput
	                label={isDistributorAccount ? text.contactName : text.name}
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
	                disabled={loading}
	              />
	            </Grid>
	            <Grid item xs={12}>
              <MDInput
                label={text.email}
                type="email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
	                disabled={loading}
	              />
	            </Grid>
	            <Grid item xs={12} md={6}>
	              <MDInput
                label={text.password}
                type="password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDInput
                label={text.confirm}
                type="password"
                fullWidth
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
	                disabled={loading}
	              />
	            </Grid>
	            </>
	            ) : null}
	            {(!isDistributorAccount || distributorStep === 1) ? (
	            <>
	            {isDistributorAccount ? (
	              <Grid item xs={12}>
	                <MDTypography variant="button" fontWeight={800} sx={{ color: "#0f172a" }}>
	                  {text.stepBusiness}
	                </MDTypography>
	              </Grid>
	            ) : null}
	            {isDistributorAccount ? (
	              <Grid item xs={12}>
	                <MDInput
	                  label={text.businessName}
	                  fullWidth
	                  value={businessName}
	                  onChange={(e) => setBusinessName(e.target.value)}
	                  disabled={loading}
	                />
	              </Grid>
	            ) : null}
	            <Grid item xs={12}>
	              <MDInput
	                label={text.phone}
	                fullWidth
	                value={phone}
	                onChange={(e) => setPhone(e.target.value)}
	                disabled={loading}
	              />
	            </Grid>
	            <Grid item xs={12}>
	              <VerifiedAddressSearch
	                rowKey="registrationAddress"
	                address={selectedAddressCandidate || {}}
	                lookup={registrationAddressLookup}
	                label={text.address}
	                disabled={loading}
	                helperText={selectedAddressPlaceId ? text.addressVerified : `${text.addressHelp} ${text.addressTypeHint}`}
	                onTyping={() => {
	                  setSelectedAddressPlaceId("");
	                  setSelectedAddress(null);
	                  setAddressLookupError("");
	                }}
	                onSelect={(option) => selectAddressCandidate(option)}
              />
            </Grid>
		            <Grid item xs={12}>
		              <MDTypography
                variant="caption"
                color={selectedAddressPlaceId ? "success" : "text"}
                sx={{ opacity: selectedAddressPlaceId ? 1 : 0.85 }}
		              >
		                {selectedAddressPlaceId ? text.addressVerified : text.addressNeedsVerification}
		              </MDTypography>
		            </Grid>
		            {selectedAddressPlaceId ? (
		              <Grid item xs={12}>
		                <Alert severity="success" icon={<Icon>check_circle</Icon>}>
		                  <strong>{text.addressVerified}:</strong> {addressLine}
		                </Alert>
		              </Grid>
		            ) : null}
	            </>
	            ) : null}
	            {isDistributorAccount && distributorStep === fiscalStepIndex && requiresMxFiscal ? (
	              <>
	                <Grid item xs={12}>
	                  <MDTypography variant="button" fontWeight={800} sx={{ color: "#0f172a" }}>
	                    {text.fiscalInfo}
	                  </MDTypography>
	                </Grid>
	                <Grid item xs={12} md={6}>
                  <MDInput
                    label={text.rfc}
                    fullWidth
                    value={rfc}
                    onChange={(e) => setRfc(String(e.target.value || "").toUpperCase())}
                    disabled={loading}
                  />
	                </Grid>
	                <Grid item xs={12} md={6}>
	                  <FormControl fullWidth>
	                    <InputLabel id="fiscal-regime-label">{text.taxSystem}</InputLabel>
	                    <Select
	                      labelId="fiscal-regime-label"
	                      label={text.taxSystem}
	                      value={regimenFiscal}
	                      onChange={(e) => setRegimenFiscal(String(e.target.value || ""))}
	                      disabled={loading}
	                    >
	                      {fiscalRegimeOptions.map((option) => (
	                        <MenuItem key={option.value} value={option.value}>
	                          {option.label}
	                        </MenuItem>
	                      ))}
	                    </Select>
	                  </FormControl>
	                </Grid>
                <Grid item xs={12}>
                  <MDInput
                    label={text.legalName}
                    fullWidth
                    value={taxLegalName}
                    onChange={(e) => setTaxLegalName(e.target.value)}
                    disabled={loading}
                  />
	                </Grid>
	                <Grid item xs={12} md={6}>
	                  <FormControl fullWidth>
	                    <InputLabel id="cfdi-use-label">{text.cfdiUse}</InputLabel>
	                    <Select
	                      labelId="cfdi-use-label"
	                      label={text.cfdiUse}
	                      value={usoCfdi}
	                      onChange={(e) => setUsoCfdi(String(e.target.value || ""))}
	                      disabled={loading}
	                    >
	                      {cfdiUseOptions.map((option) => (
	                        <MenuItem key={option.value} value={option.value}>
	                          {option.label}
	                        </MenuItem>
	                      ))}
	                    </Select>
	                  </FormControl>
	                </Grid>
                <Grid item xs={12} md={6}>
                  <MDInput
                    label={text.fiscalZip}
                    fullWidth
                    value={fiscalZip}
                    onChange={(e) => setFiscalZip(e.target.value)}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MDInput
                    label={text.billingEmail}
                    type="email"
                    fullWidth
                    value={facturacionEmail}
                    onChange={(e) => setFacturacionEmail(e.target.value)}
                    disabled={loading}
                  />
	                </Grid>
	                <Grid item xs={12}>
	                  <VerifiedAddressSearch
	                    rowKey="registrationFiscalAddress"
	                    address={selectedFiscalAddressCandidate || {}}
	                    lookup={registrationFiscalAddressLookup}
	                    label={text.fiscalAddress}
	                    disabled={loading}
	                    helperText={
	                      selectedFiscalAddressPlaceId ? text.addressVerified : `${text.addressHelp} ${text.addressTypeHint}`
	                    }
	                    onTyping={() => {
	                      setSelectedFiscalAddressPlaceId("");
	                      setSelectedFiscalAddress(null);
	                      setFiscalAddressLookupError("");
	                    }}
	                    onSelect={(option) => selectFiscalAddressCandidate(option)}
	                  />
	                </Grid>
	                <Grid item xs={12}>
	                  <MDTypography
	                    variant="caption"
	                    color={selectedFiscalAddressPlaceId ? "success" : "text"}
	                    sx={{ opacity: selectedFiscalAddressPlaceId ? 1 : 0.85 }}
		                  >
		                    {selectedFiscalAddressPlaceId ? text.addressVerified : text.addressNeedsVerification}
		                  </MDTypography>
		                </Grid>
		                {selectedFiscalAddressPlaceId ? (
		                  <Grid item xs={12}>
		                    <Alert severity="success" icon={<Icon>check_circle</Icon>}>
		                      <strong>{text.addressVerified}:</strong> {fiscalAddressLine}
		                    </Alert>
		                  </Grid>
		                ) : null}
		              </>
	            ) : null}
	            {isDistributorAccount && distributorStep === verifyStepIndex ? (
	              <>
	                <Grid item xs={12}>
	                  <MDTypography variant="button" fontWeight={800} sx={{ color: "#0f172a" }}>
	                    {text.verificationTitle}
	                  </MDTypography>
	                  <MDTypography variant="caption" color="text" sx={{ display: "block", opacity: 0.78 }}>
	                    {text.verificationHelp}
	                  </MDTypography>
	                </Grid>
	                <Grid item xs={12}>
	                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
	                    <MDInput
	                      label={text.emailCode}
	                      fullWidth
	                      value={emailVerificationCode}
	                      onChange={(e) => setEmailVerificationCode(e.target.value)}
	                      disabled={loading}
	                    />
	                    <MDButton
	                      variant="outlined"
	                      color="info"
	                      onClick={onSendCode}
	                      disabled={loading || codeLoading || !String(email || "").trim()}
	                      sx={{ minWidth: { xs: "100%", sm: 150 }, borderRadius: 2 }}
	                    >
	                      {codeLoading ? text.sendingCode : text.sendCode}
	                    </MDButton>
	                  </Stack>
	                  {codeSent ? (
	                    <MDTypography variant="caption" color="success" sx={{ display: "block", mt: 0.75 }}>
	                      {text.codeSent}
	                    </MDTypography>
	                  ) : null}
	                </Grid>
	              </>
	            ) : null}
	          </Grid>

	          <Divider sx={{ mt: 2.6, mb: 1.8, borderColor: "rgba(11,17,24,0.08)" }} />
	          <MDBox display="grid" gap={1.15}>
	            {isDistributorAccount && distributorStep > 0 ? (
	              <MDButton
	                type="button"
		                variant="outlined"
		                fullWidth
		                sx={{
		                  py: 1.25,
		                  borderRadius: 2,
		                  borderColor: "rgba(11,17,24,0.12)",
		                  color: "#0b1118",
		                  fontWeight: 900,
		                }}
	                disabled={loading}
	                onClick={() => setDistributorStep((step) => Math.max(step - 1, 0))}
	              >
	                {text.previous}
	              </MDButton>
	            ) : null}
	            <MDButton
	              type="submit"
		              variant="contained"
		              fullWidth
		              sx={{
		                py: 1.38,
		                borderRadius: 2,
		                bgcolor: "#cf241f",
		                color: "#fff",
		                fontWeight: 900,
		                boxShadow: "none",
		                "&:hover": { bgcolor: "#af1f1a", boxShadow: "none" },
		                "&.Mui-disabled": { bgcolor: "rgba(11,17,24,0.12)", color: "rgba(11,17,24,0.38)" },
		              }}
	              disabled={loading || (isDistributorAccount ? !distributorStepValid : !canSubmit)}
	            >
	              {loading
	                ? text.creating
	                : isDistributorAccount && distributorStep < distributorSteps.length - 1
	                ? text.next
	                : isDistributorAccount
	                ? text.submitApplication
	                : text.create}
	            </MDButton>
            {!isDistributorAccount ? (
              <MDBox sx={{ mt: 0.1, mb: 0.1 }}>
                {googleClientId || appleClientId || microsoftClientId ? (
                  <MDTypography
                    variant="caption"
                    color="text"
                    sx={{ display: "block", textAlign: "center", opacity: 0.78, mb: 0.65 }}
                  >
                    {isMx ? "o continuar con" : "or continue with"}
                  </MDTypography>
                ) : null}
	                <Stack direction="row" spacing={0.8} justifyContent="center" alignItems="center">
                  {googleClientId ? (
                    <MDButton
                      variant="outlined"
                      color="dark"
                      sx={socialIconButtonSx}
                      disabled={loading}
                      onClick={() => onSocialRegister("google")}
                      title="Google"
                      aria-label="Continue with Google"
                    >
                      <GoogleIcon sx={{ color: "#EA4335", fontSize: 18 }} />
                    </MDButton>
                  ) : null}
                  {appleClientId ? (
                    <MDButton
                      variant="outlined"
                      color="dark"
                      sx={socialIconButtonSx}
                      disabled={loading}
                      onClick={() => onSocialRegister("apple")}
                      title="Apple"
                      aria-label="Continue with Apple"
                    >
                      <AppleIcon sx={{ color: "#111827", fontSize: 18 }} />
                    </MDButton>
                  ) : null}
                  {microsoftClientId ? (
                    <MDButton
                      variant="outlined"
                      color="dark"
                      sx={socialIconButtonSx}
                      disabled={loading}
                      onClick={() => onSocialRegister("microsoft")}
                      title="Microsoft"
                      aria-label="Continue with Microsoft"
                    >
                      <MicrosoftMark />
                    </MDButton>
                  ) : null}
	                </Stack>
	                <MDButton
	                  variant="text"
	                  size="small"
	                  fullWidth
	                  sx={{ mt: 1, color: "#0b1118", fontWeight: 900 }}
                  onClick={() => navigate(toMobileAwarePath("/distributor/apply"))}
	                >
	                  {text.customerLinkPrefix} {text.distributorLink}
	                </MDButton>
	              </MDBox>
	            ) : null}
	            <MDButton
	              variant="outlined"
	              fullWidth
	              sx={{
	                py: 1.28,
	                borderRadius: 2,
	                borderColor: "rgba(11,17,24,0.12)",
	                color: "#0b1118",
	                fontWeight: 900,
	              }}
	              disabled={loading}
	              onClick={() => navigate(toMobileAwarePath("/authentication/sign-in"))}
            >
              {text.back}
            </MDButton>
          </MDBox>
        </MDBox>
      </Container>
    </Box>
  );
}
